/* eslint-disable max-lines */
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useAlarmGroupCamera,
  useAlarmGroupPlayback,
  useAlarmGroupStatic,
  useAlarmGroupTime,
} from '../alarm-provider/AlarmGroupProvider';
import CameraTimeline from './CameraTimeline';
import CompressedTimeline from './CompressedTimeline';
import TimeIndicators from './TimeIndicators';
import { TimelineScrollbar } from './TimelineScrollbar';
import { TimelineSeeker } from './TimelineSeeker';
import { useAutoLoadNeighbouringAlarms } from './useAutoLoadNeighbouringAlarms';
import { useTimelineZoom } from './useTimelineZoom';
import { getTruncatedText } from './utils';

interface Props {
  isCompressedTimeline: boolean;
  toggleTimeline?: () => void;
  onNeighbourLoadingChange?: (isLoading: boolean) => void;
}

export interface AlarmGroupTimelineHandle {
  resetZoom: () => void;
}

export const AlarmGroupTimeline = forwardRef<AlarmGroupTimelineHandle, Props>(
  function AlarmGroupTimeline(props, ref) {
    const { isCompressedTimeline, toggleTimeline, onNeighbourLoadingChange } =
      props;
    const { alarmCameraIds, location } = useAlarmGroupStatic();
    const { seekTo, seekerTimestamp, isSeekerPaused, toggleSeekerPaused } =
      useAlarmGroupTime();
    const {
      timelineBounds: {
        totalTimelineStartTime,
        totalTimelineEndTime,
        alarmGroupStartTime,
        alarmGroupEndTime,
      },
    } = useAlarmGroupPlayback();
    const { camToDetailsMap } = useAlarmGroupCamera();
    const timelineLabelRef = useRef<HTMLDivElement>(null);
    const timelineParentRef = useRef<HTMLDivElement>(null);
    const [localSeekerPosition, setLocalSeekerPosition] = useState<
      number | null
    >(null);

    const { requestExtension, isFetching: isFetchingNeighbours } =
      useAutoLoadNeighbouringAlarms();

    const {
      viewStart,
      viewEnd,
      thumbLeftPercent,
      thumbWidthPercent,
      zoomAtCursor,
      panByFraction,
      panToThumbLeft,
      resetZoom,
    } = useTimelineZoom({
      totalStart: totalTimelineStartTime,
      totalEnd: totalTimelineEndTime,
      onRequestExtendBounds: requestExtension,
    });

    useImperativeHandle(ref, () => ({ resetZoom }), [resetZoom]);

    useEffect(() => {
      onNeighbourLoadingChange?.(isFetchingNeighbours);
    }, [onNeighbourLoadingChange, isFetchingNeighbours]);

    const isDraggingTimeline = useRef(false);
    const dragLastX = useRef(0);

    interface HoverState {
      xPercent: number;
      yPx: number;
      timestamp: number;
    }
    const [hoverState, setHoverState] = useState<HoverState | null>(null);
    const hoverRafIdRef = useRef<number | null>(null);
    const pendingHoverRef = useRef<HoverState | null>(null);

    const flushHover = useCallback(() => {
      hoverRafIdRef.current = null;
      if (pendingHoverRef.current) {
        setHoverState(pendingHoverRef.current);
      }
    }, []);

    useEffect(() => {
      return () => {
        if (hoverRafIdRef.current !== null) {
          cancelAnimationFrame(hoverRafIdRef.current);
          hoverRafIdRef.current = null;
        }
      };
    }, []);

    const pauseSeekerIfPlaying = useCallback(() => {
      if (!isSeekerPaused) toggleSeekerPaused();
    }, [isSeekerPaused, toggleSeekerPaused]);

    const wheelCallbacksRef = useRef({
      zoomAtCursor,
      pauseSeekerIfPlaying,
    });
    useEffect(() => {
      wheelCallbacksRef.current = {
        zoomAtCursor,
        pauseSeekerIfPlaying,
      };
    });

    useEffect(() => {
      const el = timelineParentRef.current;
      if (!el) return;

      const handleWheel = (e: WheelEvent) => {
        if (e.deltaY === 0 || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
          return;
        }
        e.preventDefault();

        const rect = el.getBoundingClientRect();
        const { zoomAtCursor: zoom, pauseSeekerIfPlaying: pause } =
          wheelCallbacksRef.current;

        pause();
        const cursorFraction = (e.clientX - rect.left) / rect.width;
        zoom(cursorFraction, e.deltaY < 0);
      };

      el.addEventListener('wheel', handleWheel, { passive: false });
      return () => el.removeEventListener('wheel', handleWheel);
    }, []);

    // --- Click-and-drag pan handlers ---
    const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest('[data-seeker]')) return;
      isDraggingTimeline.current = true;
      dragLastX.current = e.clientX;
    }, []);

    const onMouseMove = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        const el = timelineParentRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const fraction = Math.max(
          0,
          Math.min(1, (e.clientX - rect.left) / rect.width)
        );
        pendingHoverRef.current = {
          xPercent: fraction * 100,
          yPx: e.clientY - rect.top,
          timestamp: viewStart + fraction * (viewEnd - viewStart),
        };
        if (hoverRafIdRef.current === null) {
          hoverRafIdRef.current = requestAnimationFrame(flushHover);
        }

        if (!isDraggingTimeline.current) return;
        const deltaFraction = -(e.clientX - dragLastX.current) / rect.width;
        dragLastX.current = e.clientX;
        panByFraction(deltaFraction);
      },
      [flushHover, panByFraction, viewStart, viewEnd]
    );

    const onMouseUp = useCallback(() => {
      isDraggingTimeline.current = false;
    }, []);

    const onMouseLeave = useCallback(() => {
      isDraggingTimeline.current = false;
      if (hoverRafIdRef.current !== null) {
        cancelAnimationFrame(hoverRafIdRef.current);
        hoverRafIdRef.current = null;
      }
      pendingHoverRef.current = null;
      setHoverState(null);
    }, []);

    // Calculate seeker position in pixels from timestamp against the view window
    const calculatedPixelPosition = useMemo(() => {
      const timelineWidth = timelineParentRef.current?.clientWidth || 0;
      const viewDuration = viewEnd - viewStart;
      if (viewDuration === 0) return 0;
      const diff = seekerTimestamp - viewStart;
      const percentage = (diff / viewDuration) * 100;
      return (percentage / 100) * timelineWidth;
    }, [seekerTimestamp, viewStart, viewEnd]);

    // Sync local position with calculated position
    useEffect(() => {
      setLocalSeekerPosition(calculatedPixelPosition);
    }, [calculatedPixelPosition]);

    const alarmGroupTimeline = useMemo(() => {
      return [alarmGroupStartTime, alarmGroupEndTime];
    }, [alarmGroupEndTime, alarmGroupStartTime]);

    const handleDragEnd = (event: DragEndEvent) => {
      const { delta } = event;
      const timelineWidth = timelineParentRef.current?.clientWidth || 0;

      if (timelineWidth === 0) return;

      // Calculate new position in pixels, then clamp to bounds
      const currentPixelPosition =
        localSeekerPosition ?? calculatedPixelPosition;
      const newPixelPosition = Math.max(
        0,
        Math.min(timelineWidth, currentPixelPosition + delta.x)
      );

      // Update local position immediately
      setLocalSeekerPosition(newPixelPosition);

      // Calculate timestamp and seek against the view window
      const percentage = (newPixelPosition / timelineWidth) * 100;
      const viewDuration = viewEnd - viewStart;
      const timeOffset = (percentage / 100) * viewDuration;
      const newTimestamp = viewStart + timeOffset;

      seekTo(newTimestamp);
    };

    const onClickCamera =
      (camId: string) => (event: React.MouseEvent<HTMLSpanElement>) => {
        if (event.metaKey || event.ctrlKey) {
          const url = `/cameras?camId=${camId}`;
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      };

    return (
      <div
        id="toolbar"
        className="relative flex items-start gap-2 overflow-x-clip pl-2 pt-8"
      >
        <div className="flex w-1/6 flex-col gap-1 pl-4" ref={timelineLabelRef}>
          {!isCompressedTimeline &&
            alarmCameraIds.map((camId) => (
              <span
                title={camToDetailsMap[camId].cameraName}
                key={camId}
                className="h-[14px] cursor-pointer overflow-hidden overflow-ellipsis whitespace-nowrap text-xs"
                tabIndex={1}
                onClick={onClickCamera(camId)}
              >
                {getTruncatedText(
                  String(camToDetailsMap[camId].cameraName),
                  timelineLabelRef
                )}
              </span>
            ))}
          {isCompressedTimeline && (
            <span className="h-4 cursor-pointer overflow-hidden overflow-ellipsis whitespace-nowrap text-xs">
              {alarmCameraIds.length} cameras
            </span>
          )}
          {toggleTimeline && (
            <button
              onClick={toggleTimeline}
              className="text-onlight-primary dark:text-ondark-primary mt-1 flex cursor-pointer flex-col items-start border-0 bg-transparent p-0 text-xs"
            >
              {isCompressedTimeline ? 'Show all' : 'Show less'}
            </button>
          )}
        </div>
        <DndContext
          onDragEnd={handleDragEnd}
          modifiers={[restrictToParentElement, restrictToHorizontalAxis]}
        >
          <div className="flex flex-grow flex-col">
            <div
              id="timeline-parent"
              className="relative mr-1 flex flex-grow cursor-grab select-none flex-col active:cursor-grabbing"
              ref={timelineParentRef}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseLeave}
            >
              <TimelineSeeker viewStart={viewStart} viewEnd={viewEnd} />
              <TimeIndicators
                startTime={viewStart}
                endTime={viewEnd}
                timezone={location?.timezone ?? 'UTC'}
                highlightTimes={alarmGroupTimeline}
              />
              {isCompressedTimeline && (
                <CompressedTimeline
                  theme="system"
                  viewStart={viewStart}
                  viewEnd={viewEnd}
                />
              )}
              {!isCompressedTimeline &&
                alarmCameraIds.map((camId) => (
                  <div key={camId} className="relative mb-1 h-[14px]">
                    <CameraTimelineWrapper
                      camId={camId}
                      viewStart={viewStart}
                      viewEnd={viewEnd}
                    />
                  </div>
                ))}
              {hoverState !== null && (
                <div
                  className="bg-ondark-bg-2 pointer-events-none absolute z-20 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] text-white"
                  style={{
                    left: `${hoverState.xPercent}%`,
                    top: `${hoverState.yPx - 22}px`,
                    transform: `translateX(${
                      hoverState.xPercent < 5
                        ? '0%'
                        : hoverState.xPercent > 95
                        ? '-100%'
                        : '-50%'
                    })`,
                  }}
                >
                  {new Date(hoverState.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: false,
                    timeZone: location?.timezone ?? 'UTC',
                  })}
                </div>
              )}
            </div>
            <div className="mr-1 mt-10">
              <TimelineScrollbar
                thumbLeftPercent={thumbLeftPercent}
                thumbWidthPercent={thumbWidthPercent}
                onThumbDragStart={pauseSeekerIfPlaying}
                onThumbDrag={panToThumbLeft}
              />
            </div>
          </div>
        </DndContext>
      </div>
    );
  }
);

export default AlarmGroupTimeline;

interface CameraTimelineWrapperProps {
  camId: string;
  viewStart?: number;
  viewEnd?: number;
}

export function CameraTimelineWrapper(props: CameraTimelineWrapperProps) {
  const { camId, viewStart, viewEnd } = props;
  return (
    <CameraTimeline
      theme="system"
      cameraId={camId}
      viewStart={viewStart}
      viewEnd={viewEnd}
    />
  );
}
